import React, { useCallback, useEffect, useState } from 'react';
import { DriveAccount, DriveItemType, DropboxDocumentEntity, MSTenants } from 'jamespot-user-api';
import { useIntl } from 'react-intl';
import { JRCIcon } from '../JRCIcon/JRCIcon';
import { JRCLoader } from '../JRCLoader/JRCLoader';
import { DriveAccountHeader } from './components/DriveAccountHeader';
import { CSSDriveApp, CSSTree, CSSTreeItem } from './drive.style';
import { JDriveApi } from 'jamespot-user-api/lib/src/apis/drive/drive';
import { DropboxDocuments } from './Dropbox/DropboxDocuments';
import { DROPBOX_SERVICE_NAME } from './drive.const';
import { useAbortController } from '../../hooks/useAbortController';

type TreeItemType = {
    title: string;
    id: string | undefined;
    name: MSTenants;
    path: string;
};

type JRCDriveDropboxProps = {
    initialFolder?: DriveItemType;
    initialFolderColor?: string;
    useNavigation?: boolean; // use the navigation allows you to navigate among the folders
    useFilter?: boolean; // use filter allows you to filter among the title of your documents
    useDocumentFilters: string[];
    onSelect?: (item: DropboxDocumentEntity) => void;
    onSelectMultiDocument?: (items: DropboxDocumentEntity[]) => void;
    onSelectDocument?: (item: DropboxDocumentEntity, lastFolder: DropboxDocumentEntity | undefined) => void;
    onError?: (code?: string) => void;
    handlers: {
        drive: Pick<
            JDriveApi,
            | 'dropboxDocuments'
            | 'groups'
            | 'tenants'
            | 'listAccount'
            | 'isAccountLogged'
            | 'checkAccountToken'
            | 'getAccount'
            | 'logout'
            | 'setDefaultAccount'
        >;
    };
    handleLogin?: boolean;
    onLogin?: (account: DriveAccount) => void;
    onLogout?: () => void;
    currentAccount?: DriveAccount;
};

export const JRCDriveDropbox = ({
    initialFolder,
    initialFolderColor,
    useDocumentFilters = [],
    useFilter = true,
    useNavigation = true,
    onSelect,
    onSelectDocument,
    onSelectMultiDocument,
    onError,
    handlers,
    handleLogin,
    onLogin,
    currentAccount,
}: JRCDriveDropboxProps) => {
    const intl = useIntl();
    const abortController = useAbortController();
    const [horizontalTree, setHorizontalTree] = useState<TreeItemType[]>([]);
    const [lastFolder, setLastFolder] = useState<DropboxDocumentEntity>();
    const [documentSelection, setDocumentSelection] = useState<DropboxDocumentEntity[]>([]);
    const [activeAccount, setActiveAccount] = useState(currentAccount);
    const [accounts, setAccounts] = useState<DriveAccount[]>();
    const [isLogged, setIsLogged] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [path, setPath] = useState<string>('');

    useEffect(() => {
        if (onSelectMultiDocument) {
            onSelectMultiDocument(documentSelection);
        }
    }, [documentSelection, onSelectMultiDocument]);

    useEffect(() => {
        if (initialFolder !== undefined) {
            setHorizontalTree([
                {
                    name: initialFolder.type === 'group' ? 'group' : 'documents',
                    title: initialFolder.title,
                    id: initialFolder.id,
                    path: '',
                },
            ]);
        }
    }, [initialFolder]);

    useEffect(() => {
        setPath(horizontalTree.at(-1)?.path ?? '');
    }, [horizontalTree]);

    const login = useCallback(
        (loggedAccount?: DriveAccount) => {
            if (!handlers.drive.listAccount || !handlers.drive.checkAccountToken || !handlers.drive.isAccountLogged)
                return;
            setIsLoading(true);
            setIsLogged(false);
            setHorizontalTree([]);
            setDocumentSelection([]);
            if (handleLogin) {
                handlers.drive
                    .listAccount(DROPBOX_SERVICE_NAME, { signal: abortController.signal })
                    .then((res) => {
                        setAccounts(res.result);
                        if (res.result.length > 0) {
                            const account =
                                (loggedAccount
                                    ? res.result.find((a) => a.id === loggedAccount.id)
                                    : res.result.find((a) => a.default)) ?? res.result[0];
                            setActiveAccount(account);
                            handlers.drive
                                .checkAccountToken(DROPBOX_SERVICE_NAME, { signal: abortController.signal })
                                .then((res) => {
                                    if (res.result) {
                                        handlers.drive
                                            .isAccountLogged(DROPBOX_SERVICE_NAME, account.id, {
                                                signal: abortController.signal,
                                            })
                                            .then((res) => {
                                                if (res.result) {
                                                    setIsLogged(res.result);
                                                    if (onLogin) {
                                                        onLogin(account);
                                                    }
                                                }
                                            })
                                            .catch(() => {
                                                if (!abortController.signal.aborted) {
                                                    setIsLogged(false);
                                                }
                                            })
                                            .finally(() => {
                                                if (!abortController.signal.aborted) {
                                                    setIsLoading(false);
                                                }
                                            });
                                    } else {
                                        setIsLogged(false);
                                        setIsLoading(false);
                                    }
                                });
                        } else {
                            setActiveAccount(undefined);
                            setIsLogged(false);
                            setIsLoading(false);
                        }
                    })
                    .catch((err) => {
                        if (!abortController.signal.aborted) {
                            if (onError) {
                                onError(err.errorMsg);
                            }
                            setIsLoading(false);
                        }
                    });
            }
        },
        [abortController.signal, handleLogin, handlers.drive, onError, onLogin],
    );

    const onLoginError = useCallback(
        (errorMsg) => {
            setIsLoading(false);
            if (onError) {
                onError(errorMsg);
            }
        },
        [onError],
    );

    useEffect(() => {
        login();
    }, [login]);

    if (handleLogin && isLoading) {
        return <JRCLoader size={'l'} />;
    }

    return (
        <CSSDriveApp>
            {handleLogin && (
                <DriveAccountHeader
                    activeAccount={activeAccount}
                    accounts={accounts}
                    onLogout={login}
                    onLogin={login}
                    isLogged={isLogged}
                    onSetDefaultAccount={(account) => {
                        setActiveAccount((prev) => (prev ? { ...prev, default: true } : prev));
                        setAccounts((prev) =>
                            prev
                                ? prev.map((a) =>
                                      a.id === account.id ? { ...a, default: true } : { ...a, default: false },
                                  )
                                : prev,
                        );
                    }}
                    handlers={handlers}
                    onLoginError={onLoginError}
                    service={DROPBOX_SERVICE_NAME}
                    onError={onError}
                />
            )}
            {(!handleLogin || isLogged) && activeAccount && (
                <>
                    <CSSTree>
                        {useNavigation && horizontalTree.length > 0 ? (
                            <CSSTreeItem onClick={() => setHorizontalTree([])}>
                                <div>{intl.formatMessage({ id: 'INPUT_FILE_ADVANCED_Dropbox' })}</div>
                                <JRCIcon name="icon-angle-right" size={12} />
                            </CSSTreeItem>
                        ) : (
                            <></>
                        )}
                        {horizontalTree.map((item, index) => {
                            return (
                                <CSSTreeItem
                                    key={index}
                                    onClick={() =>
                                        setHorizontalTree((prevTree) => prevTree.filter((_item, i) => i <= index))
                                    }>
                                    {item.title}
                                    {index < horizontalTree.length - 1 && <JRCIcon name="icon-angle-right" size={12} />}
                                </CSSTreeItem>
                            );
                        })}
                    </CSSTree>
                    <DropboxDocuments
                        path={path}
                        initialFolderColor={initialFolderColor}
                        useFilter={useFilter}
                        useNavigation={useNavigation}
                        useDocumentFilters={useDocumentFilters}
                        onSelect={typeof onSelect === 'function' ? onSelect : undefined}
                        onSelectMultiDocument={
                            onSelectMultiDocument
                                ? (doc) => {
                                      const item = documentSelection.find((el) => el.id === doc.id);
                                      if (item) {
                                          setDocumentSelection(documentSelection.filter((el) => el.id !== doc.id));
                                      } else {
                                          setDocumentSelection([...documentSelection, doc]);
                                      }
                                  }
                                : undefined
                        }
                        onSelectDocument={
                            onSelectDocument
                                ? (doc) => {
                                      if (onSelectDocument) {
                                          onSelectDocument(doc, lastFolder);
                                      }
                                  }
                                : undefined
                        }
                        onClick={(entity) => {
                            setLastFolder(entity);
                            setPath(entity._extend.path);
                            setHorizontalTree([
                                ...horizontalTree,
                                { name: 'documents', title: entity.title, id: entity.id, path: entity._extend.path },
                            ]);
                        }}
                        onError={onError}
                        handlers={handlers}
                        id={activeAccount.id}
                        service={DROPBOX_SERVICE_NAME}
                    />
                </>
            )}
        </CSSDriveApp>
    );
};
